import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { when } from "lit/directives/when.js";

import FormElement, { type FormElementProps } from "@/components/form/form-element";
import { Watch } from "@/decorators/watch";
import { filterEmptyNodes } from "@/internals/elements";
import { emit } from "@/internals/events";
import { WithTooltipMixin, type WithTooltipProps } from "@/internals/mixins/with-tooltip-mixin";

import styles from "./atlas-checkbox.scss";

import "@/components/display/atlas-icon/atlas-icon";

export type CheckboxProps = FormElementProps &
    WithTooltipProps & {
        "icon": string;
        "checked": boolean;
        "indeterminate": boolean;
        "button-style": boolean;
        "expired": boolean;
    };

/**
 * @dependency atlas-icon
 *
 * @slot - Usado para escrever a label que irá acompanhar o input
 *
 * @event {CustomEvent} atlas-checkbox-check - Evento disparado quando o checkbox é marcado
 * @event {CustomEvent} atlas-checkbox-uncheck - Evento disparado quando o checkbox é desmarcado
 * @event {CustomEvent} atlas-checkbox-change - Evento disparado quando o checkbox sofre alteração
 *
 */
@customElement("atlas-checkbox")
export default class AtlasCheckbox extends WithTooltipMixin(FormElement) {
    static styles = styles;

    /** Ícone que será renderizado ao lado da label (apenas em conjunto com o atributo button-style) */
    @property({ type: String }) icon: string = "";

    /** Indica que o checkbox está marcado */
    @property({ type: Boolean, reflect: true }) checked: boolean = false;

    /** Indica que o checkbox é indeterminado */
    @property({ type: Boolean, reflect: true }) indeterminate: boolean = false;

    /** Indica que o checkbox está expirado */
    @property({ type: Boolean, reflect: true }) expired: boolean = false;

    /** Indica que o checkbox possui o estilo de botão */
    @property({ type: Boolean, attribute: "button-style" }) buttonStyle: boolean = false;

    @state() private _hasSlottedLabel = false;

    /** @internal */
    @Watch("checked", true)
    public onChangeChecked() {
        const eventObject = {
            detail: {
                elementId: this.id,
                elementName: this.name,
                elementValue: this.value,
                checked: this.checked,
                trackDisable: true
            }
        };

        emit(this, `atlas-checkbox-${this.checked ? "check" : "uncheck"}`, eventObject);
        emit(this, "atlas-checkbox-change", eventObject);
        emit(this, "atlas-form-element-touch", { trackDisable: true });
    }

    /**
     * Obtém o valor do elemento (se estiver marcado) ou uma string vazia.
     * Se o checkbox for indeterminado, retorna "indeterminate"
     * @returns {string} O valor do elemento ou uma string vazia ou "indeterminate" (se o checkbox for indeterminado)
     * @override
     */
    public getElementValue(): string {
        if (this.indeterminate) {
            return "indeterminate";
        }

        return this.checked ? this.value : "";
    }

    /**
     * Define o foco no checkbox
     */
    public focus() {
        (this.shadowRoot.querySelector(".form-check-input, .btn-check") as HTMLInputElement).focus();
    }

    /**
     * Marca o checkbox
     */
    public check() {
        this.checked = true;
        this.indeterminate = false;
    }

    /**
     * Desmarca o checkbox
     */
    public uncheck() {
        this.checked = false;
        this.indeterminate = false;
    }

    /**
     * Alterna o estado de marcação do checkbox
     * @param checked - Indica se o checkbox deve ser marcado ou desmarcado, caso não seja definido, o estado atual será invertido
     */
    public toggleChecked(checked?: boolean) {
        if (typeof checked === "boolean") {
            this.checked = checked;
        } else {
            this.checked = !this.checked;
        }
    }

    private changeChecked() {
        this.checked = !this.checked;
        this.onChange();
    }

    private onLabelChange() {
        const slottedNodes = this.shadowRoot.querySelector("slot").assignedNodes({ flatten: true });
        const filteredNodes = filterEmptyNodes(slottedNodes);

        this._hasSlottedLabel = filteredNodes.length > 0;
    }

    private renderIcon() {
        return when(this.buttonStyle && this.icon, () => html`<atlas-icon size="3x" name="${this.icon}"></atlas-icon>`);
    }

    public render() {
        const checkContainerClass = {
            "form-check": !this.buttonStyle,
            "form-check-disabled": this.disabled,
            "has-label": this._hasSlottedLabel,
            "block": this.buttonStyle,
            "form-check-expired": this.expired
        };

        const formCheckClass = {
            "form-check-input": !this.buttonStyle,
            "form-check-input-expired": this.expired,
            "btn-check": this.buttonStyle,
            [`is-${this._status}`]: this.getShowStatus()
        };

        const labelClass = {
            "form-check-label": !this.buttonStyle,
            "btn": this.buttonStyle,
            "btn-outline-primary": this.buttonStyle,
            "btn-outline-danger": this.buttonStyle && this.getShowStatus() && this._status === "error",
            "btn-outline-warning": this.buttonStyle && this.getShowStatus() && this._status === "warning",
            "btn-outline-success": this.buttonStyle && this.getShowStatus() && this._status === "success",
            "rounded-pill": this.buttonStyle,
            "block": this.buttonStyle
        };

        return html`
            <div class="${classMap(checkContainerClass)}" data-atlas-tooltip="checkbox-tooltip">
                <input
                    class="${classMap(formCheckClass)}"
                    type="checkbox"
                    id="${this.id || "checkbox_id"}"
                    name="${this.name}"
                    .value=${ifDefined(this.value ? this.value : undefined)}
                    .checked=${live(this.checked)}
                    .indeterminate=${live(this.indeterminate)}
                    ?required=${this.required}
                    ?disabled=${this.disabled}
                    aria-checked=${this.checked ? "true" : "false"}
                    aria-labelledby="${this.id}"
                    aria-disabled="${this.disabled}"
                    aria-required="${this.required}"
                    @change="${this.changeChecked}"
                />
                <label class=${classMap(labelClass)} for="${this.id || "checkbox_id"}">
                    ${this.renderIcon()}
                    <slot @slotchange=${this.onLabelChange}></slot>
                </label>
            </div>
            ${this.renderTooltip("checkbox-tooltip")}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-checkbox": AtlasCheckbox;
    }
}
